import * as React from 'react'
import { Col, Card, Row, Divider, Typography, Switch, message, Button, Popover } from 'antd'
import { Popconfirm } from 'antd-v4'
import { ICompany, ILocation } from '@getgreenline/homi-shared'
import { inject } from 'mobx-react'
import { ExternalSourceAPI, ExternalSourceModels } from '@getgreenline/external-sources'
import moment from 'moment'
import { isNil } from 'lodash'
import { IAllAccessibleIntegration } from './index'
import { SmartImage } from '../../../components/SmartImage'
import { Routes } from '../../../constants/Routes'
import { parseErrorMsg, prepopulateEmail } from '../../../utilities/helpers'
import { CurrentUserStore } from '../../../stores/CurrentUserStore'
import { CurrentCompanyStore } from '../../../stores/CurrentCompanyStore'
import { BLAZE_ECOMMERCE, BLAZE_COMPANY, GREENLINE_LOYALTY } from '../../../constants/general'

interface IIntegrationEmailBody {
  integrationName: string
  userEmail: string
  companyId: number
  companyName: string
  externalApiAuthToken: string
  locations: ILocation[]
}

interface Props {
  company: ICompany
  enabledExternalSource:
    | ExternalSourceModels.ICompanyExternalSourceContract
    | IAllAccessibleIntegration
  setSelectedExternalSource: () => void
  setCompanyExternalSource: (
    companyExternalSource: ExternalSourceModels.ICompanyExternalSourceContract,
  ) => void
}

interface InjectedProps {
  currentUserStore?: CurrentUserStore
  currentCompanyStore?: CurrentCompanyStore
}

interface States {
  isSwitchChecked: boolean
}

@inject('currentCompanyStore', 'currentUserStore')
export class IntegrationCard extends React.Component<Props & InjectedProps, States> {
  user = this.props.currentUserStore!.currentUser!
  showToggle =
    this.props.enabledExternalSource.name.toLowerCase() !== BLAZE_ECOMMERCE.toLowerCase().trim() &&
    this.props.enabledExternalSource.name.toLowerCase() !==
      GREENLINE_LOYALTY.toLowerCase().trim() &&
    this.props.enabledExternalSource.isEnabled !== null

  constructor(props: Props) {
    super(props)
    this.state = {
      isSwitchChecked: this.props.enabledExternalSource.isEnabled || false,
    }
  }

  get settingsLink() {
    const { company, enabledExternalSource } = this.props
    const { canAccessQuickBooks, canAccessWordPress } = company.permissions

    switch (enabledExternalSource.name) {
      case 'WooCommerce':
        return {
          showSettingsPageLink: canAccessWordPress,
          link: Routes.DASHBOARD_INTEGRATIONS_WOOCOMMERCE_PRODUCTS(company.id),
        }
      case 'Quickbooks':
        return {
          showSettingsPageLink: canAccessQuickBooks,
          link: Routes.DASHBOARD_INTEGRATIONS_QUICKBOOKS_ONLINE_SETTINGS(company.id),
        }
      default:
        return {
          showSettingsPageLink: false,
        }
    }
  }

  createIntegrationEmail = (emailDetails: IIntegrationEmailBody): string => {
    const message = [
      `Hello ${emailDetails.integrationName} Integration Team,\n\n`,
      `I'm reaching out from ${emailDetails.companyName}. I'd like to connect my ${BLAZE_COMPANY} account to ${emailDetails.integrationName}.\n`,
      `Below are my company details and API information.\n\n`,
      `Date: ${moment().format('YYYY/MM/DD hh:mma')}\n`,
      `Authorizing user: ${emailDetails.userEmail}\n`,
      `Company name: ${emailDetails.companyName}\n`,
      `Company ID: ${emailDetails.companyId}\n\n`,
      `Location ID’s\n`,
    ]

    emailDetails.locations.forEach((location) => {
      message.push(`${location.name}: ${location.id}\n`)
    })

    message.push(`\nBest,\n`, `${emailDetails.companyName}`)

    return message.join('')
  }

  handleIntegrationToggle = async (enabled: boolean): Promise<void> => {
    const { currentCompanyStore, company, enabledExternalSource, setCompanyExternalSource } =
      this.props

    // we don't want to run this function with the default integrations
    // (Merrco, Moneris, Leafly, QuickBooks): IAllAccessibleIntegration
    if (enabledExternalSource.id === null) return

    try {
      if (enabled && enabledExternalSource.email) {
        const mailToHref = prepopulateEmail({
          mailto: enabledExternalSource.email,
          subject: `Integration with ${enabledExternalSource.name}`,
          body: isNil(enabledExternalSource.isEnabled) ? '' : this.integrationMailBody,
        })

        window.location.href = mailToHref
      }

      this.setState({
        isSwitchChecked: enabled,
      })

      const updatedCompanyExternalSource = await ExternalSourceAPI.editCompanyExternalSource(
        company.id,
        {
          ...enabledExternalSource,
          isEnabled: enabled,
        },
      )

      if (updatedCompanyExternalSource.name === 'Weedmaps') {
        currentCompanyStore!.getWeedmapsIntegrationStatus()
      }
      setCompanyExternalSource(updatedCompanyExternalSource)
      message.success(`${updatedCompanyExternalSource.name} ${enabled ? 'enabled' : 'disabled'}`)
    } catch (error) {
      message.error(
        parseErrorMsg(error) ||
          `An error occurred while ${enabled ? 'enabling' : 'disabling'} the integration`,
      )
    }
  }

  get merrcoEmailTemplate() {
    const { company } = this.props

    const message = [
      `Hello Merrco Team,\n\n`,
      `I'm reaching out from ${company.name}. I'm interested in setting up Merrco in my store and would like to speak to someone on your team for further assistance.\n\n`,
      `Best\n`,
      `${company.name}`,
    ]

    return message.join('')
  }

  get integrationMailBody() {
    const { company, enabledExternalSource } = this.props

    if (enabledExternalSource.name === 'Merrco') {
      return this.merrcoEmailTemplate
    }

    if (enabledExternalSource.id === null) {
      return ''
    }

    return this.createIntegrationEmail({
      integrationName: enabledExternalSource.name,
      userEmail: this.user.email || '',
      companyId: company.id,
      companyName: company.name,
      externalApiAuthToken: company.externalApiAuthToken,
      locations: this.props.currentCompanyStore!.locations || [],
    })
  }

  get togglePromptMessage() {
    const { enabledExternalSource } = this.props

    if (enabledExternalSource.email) {
      return `To complete this integration the company needs to be notified with API keys information. Would you like to send an email now?`
    }

    return `To complete this integration, please visit the company's website and contact them directly to get your account set up.`
  }

  render() {
    const { enabledExternalSource, setSelectedExternalSource } = this.props

    return (
      <Col xxl={8} xl={8} lg={12} md={24} sm={24} xs={24} className='d-flex'>
        <div className='pr-3 pb-3' style={{ display: 'flex', flexGrow: 1 }}>
          <Card
            bordered={false}
            style={{ flexGrow: 1 }}
            bodyStyle={{ flexGrow: 1, padding: '1rem' }}
          >
            <Row>
              {enabledExternalSource.logoImageUrl && (
                <Col span={4} className='mr-2'>
                  <SmartImage
                    className='no-minimum-dimension'
                    src={enabledExternalSource.logoImageUrl}
                  />
                </Col>
              )}
              <Col span={enabledExternalSource.logoImageUrl ? 19 : 24}>
                <div className='d-flex'>
                  <div style={{ flexGrow: 1, fontSize: '16px' }}>{enabledExternalSource.name}</div>

                  <div className='spaced-button-wrapper'>
                    {enabledExternalSource.homepage && (
                      <Popover title='Homepage' content='Integration homepage'>
                        <Button
                          shape='circle'
                          icon='home'
                          href={enabledExternalSource.homepage}
                          target='_blank'
                        />
                      </Popover>
                    )}

                    {enabledExternalSource.setupUrl && (
                      <Popover
                        title='Help page'
                        content={`Find out how to set up ${enabledExternalSource.name}`}
                      >
                        <Button
                          shape='circle'
                          icon='question-circle'
                          href={enabledExternalSource.setupUrl}
                          target='_blank'
                        />
                      </Popover>
                    )}

                    {enabledExternalSource.email && (
                      <Popover title='Email' content='Contact us to get started'>
                        <Button
                          shape='circle'
                          icon='mail'
                          href={prepopulateEmail({
                            mailto: enabledExternalSource.email,
                            subject: `Integration with ${enabledExternalSource.name}`,
                            body: this.integrationMailBody,
                          })}
                        />
                      </Popover>
                    )}

                    {this.settingsLink.showSettingsPageLink && this.settingsLink.link && (
                      <Popover title='Settings page' content='Activate the integration'>
                        <Button shape='circle' icon='setting' href={this.settingsLink.link} />
                      </Popover>
                    )}
                  </div>
                </div>

                <div style={{ fontSize: '12px' }}>{enabledExternalSource.categoryName}</div>

                <Typography.Paragraph
                  className='mt-2 mb-2'
                  ellipsis={{ rows: 4, expandable: true }}
                >
                  {enabledExternalSource.description}
                </Typography.Paragraph>

                <Divider className='mt-2 mb-2' />

                {/* Show this switch after customers have full control over enabling/disabling integrations */}
                {this.showToggle ? (
                  <Row className='d-flex justify-content-between mb-2'>
                    <Col span={12}>Activate the integration</Col>

                    <Col span={12} className='text-right'>
                      {enabledExternalSource.name === 'Weedmaps' ? (
                        <Switch
                          checked={this.state.isSwitchChecked}
                          onChange={async (bool) => {
                            this.handleIntegrationToggle(bool)
                          }}
                        />
                      ) : (
                        <Popconfirm
                          title={this.togglePromptMessage}
                          onConfirm={() => this.handleIntegrationToggle(true)}
                          okText='OK'
                          getPopupContainer={(trigger) =>
                            trigger.closest('#app-main-view') as HTMLElement
                          }
                          cancelText='Cancel'
                          disabled={this.state.isSwitchChecked}
                        >
                          <Switch
                            checked={this.state.isSwitchChecked}
                            onChange={async (bool) => {
                              !bool && this.handleIntegrationToggle(bool)
                            }}
                          />
                        </Popconfirm>
                      )}
                    </Col>
                  </Row>
                ) : null}

                {enabledExternalSource.permissions.length > 0 && (
                  <Row className='d-flex justify-content-between'>
                    <Col span={12}>Allowed permissions</Col>
                    <Col span={12} className='text-right'>
                      <a onClick={setSelectedExternalSource}>Show</a>
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>
          </Card>
        </div>
      </Col>
    )
  }
}
